// src/pages/profile-settings/patient/Conditions.jsx
import React from "react";
import {
  Search as SearchIcon,
  Plus,
  CircleX as CircleXIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import Card from "@/components/ui/Card.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Divider from "@/components/ui/Divider.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import TapButton from "@/pages/onboarding/_shared/TapButton.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";
import { loadConditions, normalizeConditionList } from "@/utils/conditions";
import { shouldHideUntranslatedCatalog } from "@/utils/i18nGuardrails.js";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";

const COMPLETION_SAFE_CONDITIONS = ["None", "Other", "Don't know"];

const FRENCH_SAFE_CONDITIONS = {
  None: "Aucune",
  Other: "Autre",
  "Don't know": "Je ne sais pas",
};

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function getConditionDisplayLabel(value, languageCode) {
  const text = String(value || "").trim();
  if (!isFrenchLanguageCode(languageCode)) return text;
  return FRENCH_SAFE_CONDITIONS[text] || text;
}

/**
 * Patient Settings → Medical conditions
 * - Settings shell + TopBarCenter + global SaveChip
 * - Hydrates from patientIntroPreview.conditions
 * - Persists back to patientIntroPreview on Save
 * - Unsaved-changes confirm dialog on close
 */
export default function ConditionsSettings() {
  const nav = useNavigate();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const [all, setAll] = React.useState([]);
  const [query, setQuery] = React.useState("");

  // ---- Seed from intro preview --------------------------------------------
  const preview = React.useMemo(() => loadPatientIntroPreview() || {}, []);

  const initialSelected = React.useMemo(() => {
    const raw = preview.conditions;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return normalizeConditionList(raw);
    }
    if (typeof raw === "string") {
      return normalizeConditionList(
        raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      );
    }
    return [];
  }, [preview]);

  const initialKey = React.useMemo(
    () => JSON.stringify([...initialSelected].slice().sort()),
    [initialSelected]
  );

  const [localSelected, setLocalSelected] = React.useState(initialSelected);
  const [seedSelected, setSeedSelected] = React.useState(initialSelected);
  const [showConfirmLeave, setShowConfirmLeave] = React.useState(false);

  // snackbars
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  // keep local in sync if preview changes between mounts (rare)
  React.useEffect(() => {
    setSeedSelected(initialSelected);
    setLocalSelected(initialSelected);
  }, [initialKey]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDbIfNeeded() {
      if (initialSelected.length > 0) return;

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (!alive || authErr || !user?.id) return;

      const { data: row, error: rowErr } = await supabase
        .from("patient_profiles")
        .select("medical_conditions")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!alive || rowErr || !row) return;

      const parsed = String(row.medical_conditions || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      const deduped = normalizeConditionList(parsed);
      if (!deduped.length) return;

      setSeedSelected(deduped);
      setLocalSelected(deduped);
      savePatientIntroPreview({
        ...(loadPatientIntroPreview() || {}),
        conditions: deduped,
      });
    }

    hydrateFromDbIfNeeded();
    return () => {
      alive = false;
    };
  }, [initialSelected.length]);

  // ---- Load conditions once -----------------------------------------------
  React.useEffect(() => {
    let ok = true;
    (async () => {
      const names = await loadConditions();
      if (ok) setAll(names);
    })();
    return () => {
      ok = false;
    };
  }, []);

  const allOptions = React.useMemo(() => {
    const merged = normalizeConditionList([...all, ...seedSelected, ...localSelected]);
    const standard = merged
      .filter((item) => !COMPLETION_SAFE_CONDITIONS.includes(item))
      .sort((a, b) => a.localeCompare(b));
    return [...COMPLETION_SAFE_CONDITIONS, ...standard];
  }, [all, localSelected, seedSelected]);

  const q = query.trim().toLowerCase();
  const filtered = q ? allOptions.filter((n) => n.toLowerCase().includes(q)) : allOptions;
  const hideCatalog = isFrench || shouldHideUntranslatedCatalog(languageCode);
  const visibleOptions = hideCatalog
    ? filtered.filter(
        (item) =>
          COMPLETION_SAFE_CONDITIONS.includes(item) ||
          seedSelected.includes(item) ||
          localSelected.includes(item)
      )
    : filtered;

  // ---- Dirty tracking for Save + dialog -----------------------------------
  const seedKey = React.useMemo(
    () => JSON.stringify([...seedSelected].slice().sort()),
    [seedSelected]
  );

  const sortedLocalKey = React.useMemo(
    () => JSON.stringify([...localSelected].slice().sort()),
    [localSelected]
  );
  const dirty = sortedLocalKey !== seedKey;
  const canSave = dirty && localSelected.length > 0;

  const commit = (next) => {
    setLocalSelected(normalizeConditionList(next));
  };

  const toggle = (name) => {
    const canonical = normalizeConditionList([name])[0] || "";
    if (!canonical) return;
    const isSpecial = COMPLETION_SAFE_CONDITIONS.includes(canonical);

    setLocalSelected((prev) => {
      const key = canonical.toLowerCase();
      const has = prev.some((x) => String(x).toLowerCase() === key);
      let next;
      if (has) {
        next = prev.filter((x) => String(x).toLowerCase() !== key);
      } else if (isSpecial) {
        next = [canonical];
      } else {
        next = [
          ...prev.filter((x) => !COMPLETION_SAFE_CONDITIONS.includes(String(x))),
          canonical,
        ];
      }
      return next;
    });
  };

  const addCustom = () => {
    const raw = query.trim();
    if (!raw) return;

    const pretty = normalizeConditionList([raw.replace(/\s+/g, " ").trim()])[0];
    if (!pretty) return;
    if (!all.some((n) => n.toLowerCase() === pretty.toLowerCase())) {
      setAll((prev) => [pretty, ...prev]);
    }
    if (!localSelected.some((n) => n.toLowerCase() === pretty.toLowerCase())) {
      commit([
        pretty,
        ...localSelected.filter(
          (item) => !COMPLETION_SAFE_CONDITIONS.includes(String(item))
        ),
      ]);
    }
    setQuery("");
  };

  const addCustomRef = React.useRef(addCustom);
  React.useEffect(() => {
    addCustomRef.current = addCustom;
  }, [addCustom]);

  const handlePlusClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addCustomRef.current();
  };

  // ---- Save / Close --------------------------------------------------------
  async function handleSave() {
    if (!canSave) return;

    try {
      const normalizedSelected = normalizeConditionList(localSelected);
      const current = loadPatientIntroPreview() || {};
      const next = {
        ...current,
        conditions: normalizedSelected,
      };
      savePatientIntroPreview(next);

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (!authErr && user?.id) {
        const { error: upErr } = await supabase
          .from("patient_profiles")
          .upsert(
            {
              user_id: user.id,
              medical_conditions: normalizedSelected.join(", "),
            },
            { onConflict: "user_id" }
          );

        if (upErr) throw upErr;

        try {
          await refreshProfile?.();
        } catch {
          // non-fatal
        }
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (err) {
      console.error("[ConditionsSettings] Save error:", err);
      setSaveError(
        t(
          "settings.subflows.conditions.saveError",
          isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again."
        )
      );
    }
  }

  function handleClose() {
    if (!dirty) {
      nav(PATIENT_SETTINGS_PROFILE_ROUTE);
    } else {
      setShowConfirmLeave(true);
    }
  }

  // -------------------------------------------------------------------------
  return (
    <SettingsScreenShell>
      <TopBarCenter
        title={t("settings.subflows.conditions.title", isFrench ? "Antécédents médicaux" : "Medical conditions")}
        onBack={handleClose}
        close
        right={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      <div className="mt-3">
        <FolaOnboardingCard role="patient" />
      </div>

      {/* Search row */}
      <div className="mt-3 flex items-center gap-3">
        <SearchIcon
          className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
          aria-hidden
        />

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(
            "settings.subflows.conditions.searchPlaceholder",
            isFrench ? "Rechercher ou ajouter" : "Search or add"
          )}
          inputMode="search"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          className="flex-1 bg-transparent outline-none border-0 focus:outline-none focus:ring-0
                     text-[16px] leading-6 placeholder:text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
        />

        {!!q && (
          <TapButton
            ariaLabel={t("settings.subflows.conditions.clearSearch", isFrench ? "Effacer la recherche" : "Clear search")}
            onTap={() => setQuery("")}
            className="shrink-0 grid place-items-center"
          >
            <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
          </TapButton>
        )}

        <button
          type="button"
          onMouseDown={handlePlusClick}
          onTouchStart={handlePlusClick}
          onClick={handlePlusClick}
          className="shrink-0 grid place-items-center rounded-full
                     w-8 h-8 border border-[rgba(24,50,119,0.16)]
                     hover:bg-[rgba(24,50,119,0.04)]
                     focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
          aria-label={t("settings.subflows.conditions.addAria", isFrench ? "Ajouter une affection" : "Add condition")}
        >
          <Plus className="h-4 w-4 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
        </button>
      </div>

      <Divider variant="outline" full className="mt-4 mb-6" />

      {/* Card list – rounded-2xl (12px token in your system) */}
      <Card className="mt-3 relative z-10 overflow-hidden">
        {visibleOptions.map((name, idx) => {
          const checked = localSelected.includes(name);
          return (
            <div key={`${name}-${idx}`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggle(name)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggle(name)
                }
                className="w-full flex items-center"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                }}
              >
                <div className="flex-1 pl-5 pr-4 py-4 text-left">
                  <span className="text-body-large text-[rgb(var(--clr-on-surface,4,8,18))]">
                    {getConditionDisplayLabel(name, languageCode)}
                  </span>
                </div>
                <div className="mr-4">
                  <Checkbox
                    checked={checked}
                    onChange={(next) => {
                      if (next !== checked) toggle(name);
                    }}
                  />
                </div>
              </div>

              {idx < visibleOptions.length - 1 && (
                <Divider inset variant="outlineVariant" />
              )}
            </div>
          );
        })}

        {visibleOptions.length === 0 && (
          <div className="px-5 py-6 text-body-medium text-on-surface-var">
            {hideCatalog && !q
              ? t(
                  "settings.subflows.conditions.catalogHidden",
                  isFrench
                    ? "Saisissez une affection et appuyez sur + pour l’ajouter. Les suggestions non traduites sont masquées dans cette langue."
                    : "Type a condition and tap + to add it. Untranslated suggestions are hidden on this screen in this language.",
                )
              : t(
                  "settings.subflows.conditions.noMatchesAdd",
                  isFrench ? "Aucun résultat. Appuyez sur + pour ajouter « {query} »." : "No matches. Tap + to add “{query}”.",
                  { query: query.trim() },
                )}
          </div>
        )}
      </Card>

      {/* Unsaved-changes confirm */}
      {showConfirmLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowConfirmLeave(false)}
          />
          <div
            className="relative w-[312px] max-w-[560px] min-w-[288px] bg-[var(--Schemes-Surface,#FFFFFF)] rounded-[28px] overflow-hidden
                       shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_0px_0px_1px_rgba(24,50,119,0.10)]"
          >
            <div className="px-6 pt-6">
              <div className="text-headline-small text-on-surface">
                {t("settings.subflows.discardTitle", isFrench ? "Abandonner les modifications ?" : "Discard changes?")}
              </div>
              <div className="text-body-medium text-on-surface-var mt-4">
                {t(
                  "settings.subflows.conditions.discardBody",
                  isFrench
                    ? "Vous avez des modifications d’antécédents non enregistrées. Voulez-vous fermer sans enregistrer ?"
                    : "You have unsaved medical condition changes. Are you sure you want to close without saving?"
                )}
              </div>
            </div>

            <div className="p-4 flex gap-3">
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
                  color: "rgb(var(--afrimed-primary))",
                }}
                onClick={() => setShowConfirmLeave(false)}
              >
                {t("common.keepEditing", isFrench ? "Continuer la modification" : "Keep editing")}
              </button>
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-Critical,#F8533A) 12%, transparent)",
                  color: "var(--Schemes-Critical,#F8533A)",
                }}
                onClick={() => {
                  setShowConfirmLeave(false);
                  nav(PATIENT_SETTINGS_PROFILE_ROUTE);
                }}
              >
                {t("common.discard", isFrench ? "Abandonner" : "Discard")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global “Changes saved” Snackbar */}
      <Snackbar open={saved} onClose={() => setSaved(false)} duration={2200}>
        {t("common.changesSaved", isFrench ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>

      {saveError && (
        <Snackbar
          open={Boolean(saveError)}
          onClose={() => setSaveError("")}
          duration={2800}
          variant="error"
        >
          {saveError || t("settings.subflows.conditions.saveError", isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again.")}
        </Snackbar>
      )}
    </SettingsScreenShell>
  );
}
